import { useContext, useEffect, useState } from "react";
import { UserStore } from "../..";
import BreadCrumbs from "../../components/breadcrumsb";
import { PageContainer } from "../../components/page";
import {
  deleteMobileAttractor,
  getMobileAttractors,
  updateMobileAttractor,
} from "../../api/new-api";
import { PEProgressBar } from "../../components/linear-progress";
import { runAuthHandler } from "../util";
import { useNavigate } from "react-router";
import MobileAttractorsContainer from "./MobileAttractorsContainer";
import { observer } from "mobx-react";
import { PhonePreview } from "../../components/PhonePreview";
import { CloudArrowUpIcon } from "@heroicons/react/24/solid";
import { userState } from "../../state/UserState";
import { MobileAttractor, UpdateMobileAttractorRequest } from "../../types";

const fetchData = async ({
  setData,
  setLoading,
  setError,
}: {
  setData: (data: MobileAttractor[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
}) => {
  setError("");
  try {
    const res = await getMobileAttractors();
    if (res.data) {
      setData(res?.data?.data?.attractors || []);
    } else {
      setData([]);
    }
  } catch (error) {
    setError("Error getting mobile attractors");
  } finally {
    setLoading(false);
  }
};

const PAGE_TITLE = "Mobile Attractors";

const MobileAttractorPage = observer(() => {
  const navigate = useNavigate();
  const userStore = useContext(UserStore);
  const { selectedVenue } = userStore;

  const [loading, setLoading] = useState<boolean>(true);
  const [operationLoading, setOperationLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<MobileAttractor[]>([]);
  const [weekCount, setWeekCount] = useState<number>(0);
  const [allCount, setAllCount] = useState<number>(0);
  const [expiredCount, setExpiredCount] = useState<number>(0);
  const [upcomingCount, setUpcomingCount] = useState<number>(0); // New state for upcoming count
  const [currentAttractors, setCurrentAttractors] = useState<MobileAttractor[]>(
    []
  );
  const [filteredData, setFilteredData] = useState<MobileAttractor[]>([]);
  const [activeFilter, setActiveFilter] = useState<
    "week" | "all" | "expired" | "upcoming"
  >("week");

  const resetData = () => {
    setData([]);
    setFilteredData([]);
    setWeekCount(0);
    setAllCount(0);
    setExpiredCount(0);
    setUpcomingCount(0); // Reset upcoming count
  };

  const getAttractors = async () => {
    fetchData({ setData, setLoading, setError });
  };

  const refreshAttractors = async () => {
    setError("");
    try {
      const res = await getMobileAttractors();
      if (res.data) {
        setData(res?.data?.data?.attractors || []);
      } else {
        setData([]);
      }
    } catch (error) {
      setError("Error getting mobile attractors");
    }
  };

  const onUpdate = async (attractor: any) => {
    setOperationLoading(true);
    try {
      const updateData: UpdateMobileAttractorRequest = {
        mobileAttractorId: attractor.mobileAttractorId,
        displayText: attractor.displayText,
        displayLinkUrl: attractor.displayLinkUrl,
        startDateEpoch: attractor.startDateEpoch,
        endDateEpoch: attractor.endDateEpoch,
        dayVisibility: attractor.dayVisibility,
        specificUserTier: attractor.tierLevelText ?? undefined,
      };
      await updateMobileAttractor(updateData);
      await refreshAttractors();
    } catch (error) {
      console.error("Error updating mobile attractor", error);
    } finally {
      setOperationLoading(false);
    }
  };

  const onDelete = async (id: string | number) => {
    setOperationLoading(true);
    try {
      await deleteMobileAttractor({ attractorId: id });
      await refreshAttractors();
    } catch (error) {
      console.error("Error deleting mobile attractor", error);
    } finally {
      setOperationLoading(false);
    }
  };

  const filterAttractors = () => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    // Helper function to sort by sortKey in reverse order (higher sortKey first)
    const sortBySortKey = (banners: MobileAttractor[]) => {
      return banners.sort((a, b) => {
        const aKey = a.sortKey ?? Number.MAX_SAFE_INTEGER;
        const bKey = b.sortKey ?? Number.MAX_SAFE_INTEGER;
        return bKey - aKey; // Reverse order: higher sortKey first
      });
    };

    const todayAttractors = data.filter((banner) => {
      const today = new Date();
      const startDate = banner.startDateEpoch
        ? new Date(banner.startDateEpoch)
        : null;
      const endDate = banner.endDateEpoch
        ? new Date(banner.endDateEpoch)
        : null;

      // Check if today falls within the attractor's active date range
      if (startDate && endDate) {
        return today >= startDate && today <= endDate;
      } else if (startDate) {
        return today >= startDate;
      } else if (endDate) {
        return today <= endDate;
      }
      // If no dates are set, consider it active today
      return true;
    });
    setCurrentAttractors(sortBySortKey(todayAttractors));

    const weekBanners = data.filter((banner) => {
      const today = new Date();
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);

      const startDate = banner.startDateEpoch
        ? new Date(banner.startDateEpoch)
        : null;
      const endDate = banner.endDateEpoch
        ? new Date(banner.endDateEpoch)
        : null;

      // Check if the attractor has ANY overlap with the last 7 days
      if (startDate && endDate) {
        // Attractor has both start and end dates
        // Check if it overlaps with the last 7 days period
        return startDate <= today && endDate >= weekAgo;
      } else if (startDate) {
        // Attractor has only start date (no end date)
        // Check if it started within the last 7 days or before today
        return startDate <= today;
      } else if (endDate) {
        // Attractor has only end date (no start date)
        // Check if it ended within the last 7 days or after today
        return endDate >= weekAgo;
      }
      // If no dates are set, consider it active
      return true;
    });
    const sortedWeekBanners = sortBySortKey(weekBanners);

    const expiredBanners = data.filter((banner) => {
      const endDate = banner.endDateEpoch
        ? new Date(banner.endDateEpoch)
        : null;
      return endDate && endDate < today;
    });
    const sortedExpiredBanners = sortBySortKey(expiredBanners);

    const upcomingBanners = data.filter((banner) => {
      const startDate = banner.startDateEpoch
        ? new Date(banner.startDateEpoch)
        : null;
      return startDate && startDate > today;
    });
    const sortedUpcomingBanners = sortBySortKey(upcomingBanners);

    setWeekCount(sortedWeekBanners.length);
    setAllCount(data.length);
    setExpiredCount(sortedExpiredBanners.length);
    setUpcomingCount(sortedUpcomingBanners.length);

    if (activeFilter === "week") {
      setFilteredData(sortedWeekBanners);
    } else if (activeFilter === "all") {
      setFilteredData(sortBySortKey([...data])); // Sort a copy of data
    } else if (activeFilter === "expired") {
      setFilteredData(sortedExpiredBanners);
    } else if (activeFilter === "upcoming") {
      setFilteredData(sortedUpcomingBanners);
    }
  };

  useEffect(() => {
    filterAttractors();
  }, [data, activeFilter]);

  useEffect(() => {
    resetData();
    runAuthHandler({ userStore, navigate });
    getAttractors();
  }, [selectedVenue]);

  console.log(currentAttractors);

  return (
    <PageContainer>
      {/* Operation Loading Overlay */}
      {operationLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-8 shadow-xl">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <p className="text-lg font-medium text-gray-700">Processing...</p>
            </div>
          </div>
        </div>
      )}

      <BreadCrumbs
        routes={[
          { path: "/home", breadcrumbName: "Home" },
          { path: "/mobile-attractors", breadcrumbName: "Mobile Attractors" },
        ]}
      />
      <div className="text-3xl mb-6 text-center font-bold">{PAGE_TITLE}</div>

      {loading && <PEProgressBar />}

      {!loading && (
        <div className="my-5">
          <div className={"flex bg-slate-100 p-10 justify-center gap-20 "}>
            <div className={"flex"}>
              <PhonePreview
                userState={userState}
                myClubPromotions={
                  currentAttractors.map((attractor) => ({
                    fullscreenS3Key: attractor.fullscreenS3Key || "",
                    thumbnailS3Key: attractor.thumbnailS3Key || "",
                    displayText: attractor.displayText || "",
                    mobileAttractorId: attractor.mobileAttractorId,
                    externalLinkUrl: attractor.externalLinkUrl,
                    sortKey: attractor.sortKey,
                  })) as any
                }
                screenType={"MY_CLUB"}
              />
            </div>

            <div
              onClick={() => navigate("/mobile-attractors/create")}
              className={
                "hover:opacity-75 hover:cursor-pointer flex-cen-stack border-4 p-10 text-center"
              }
            >
              <div className="flex flex-col items-center gap-1">
                <p className={"text-md font-bold"}>
                  Click here to start adding a Mobile Attractor
                </p>
                <p className="text-md font-light">upload specs:</p>
                <p className="text-md font-light">
                  Thumbnail: Width to Height Ratio of 2:1
                </p>
                <p className="text-md font-light">
                  Fullscreen: Width to Height ratio of 9:16
                </p>
                <CloudArrowUpIcon className={"h-10 w-10 text-blue-500"} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
        <h5 className="text-center mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          Showing Today
        </h5>
        <p className="text-center font-normal text-gray-700 dark:text-gray-400">
          Here are the current attractors:
        </p>

        <div className="mt-4">
          <MobileAttractorsContainer
            errorMessage="No current attractors found"
            data={currentAttractors}
            getAttractors={getAttractors}
            onDelete={onDelete}
            onUpdate={onUpdate}
            objectKey="mobileAttractorId"
            showPromotionDisplayName={true}
            showTierRestrictions={true}
            showSwapOrder={true}
            setAttractorsData={setData}
          />
        </div>
      </div>

      {!loading && (
        <div className="w-full">
          <div className="mt-6 flex justify-center gap-4 mb-6">
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "week"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("week")}
            >
              Showing This Week ({weekCount})
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "all"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("all")}
            >
              All Attractors ({allCount})
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "expired"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("expired")}
            >
              Expired Attractors ({expiredCount})
            </button>
            <button
              className={`px-4 py-2 rounded-lg ${
                activeFilter === "upcoming"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200"
              }`}
              onClick={() => setActiveFilter("upcoming")}
            >
              Upcoming Attractors ({upcomingCount})
            </button>
          </div>

          <div className="block w-full p-6 bg-gray-100 border border-gray-200 rounded-lg shadow">
            <h5 className="text-center mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              {activeFilter === "week"
                ? "Showing This Week"
                : activeFilter === "all"
                ? "All Attractors"
                : activeFilter === "expired"
                ? "Expired Attractors"
                : "Upcoming Attractors"}
            </h5>
            <p className="text-center font-normal text-gray-700 dark:text-gray-400">
              {activeFilter === "week"
                ? "Attractors from the last 7 days"
                : activeFilter === "all"
                ? "All showing and finished attractors"
                : activeFilter === "expired"
                ? "Finished attractors"
                : "Attractors scheduled to start in the future"}
            </p>

            <div className="mt-4">
              <MobileAttractorsContainer
                errorMessage={`No ${
                  activeFilter === "week"
                    ? "current"
                    : activeFilter === "all"
                    ? "other"
                    : activeFilter === "expired"
                    ? "expired"
                    : "upcoming"
                } attractors found`}
                data={filteredData}
                getAttractors={getAttractors}
                onDelete={onDelete}
                onUpdate={onUpdate}
                objectKey="mobileAttractorId"
                showPromotionDisplayName={true}
                showTierRestrictions={true}
                setAttractorsData={setData}
                showSwapOrder={true}
              />
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
});

export default MobileAttractorPage;
